import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { apiFetch, API, SESSION_KEY } from "@/lib/gp";

import { captureAttribution } from "@/lib/marketingAnalytics";

const AuthContext = createContext(null);
function saveSession(user) {
  if (user && user.session_token) {
    window.localStorage.setItem(SESSION_KEY, user.session_token);
    const safeUser = { ...user };
    delete safeUser.session_token;
    return safeUser;
  }
  return user;
}

function formatErr(detail, fallback = "Something went wrong. Please try again.") {
  if (detail == null) return fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && e.msg) || JSON.stringify(e)).join(" ");
  if (detail.msg) return detail.msg;
  return fallback;
}

async function call(path, body) {
  const res = await apiFetch(path, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(formatErr(data.detail));
  return data;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = loading, false = guest, object = authed
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const res = await apiFetch("/auth/me");
      if (res.ok) {
        setUser(saveSession(await res.json()));
        return;
      }
      // Only a genuine "not authenticated" response should clear the persisted
      // bearer token — that token exists specifically to survive cases where the
      // cookie isn't delivered (blocked third-party cookies, a brief network blip),
      // so wiping it on every failure would defeat its purpose.
      if (res.status === 401) {
        window.localStorage.removeItem(SESSION_KEY);
      }
      setUser(false);
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const hash = window.location.hash;
    if (hash && hash.includes("session_id=")) {
      window.history.replaceState(null, "", `${window.location.pathname}${window.location.search}`);
    }
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const u = await call("/auth/login", { email, password });
    const safeUser = saveSession(u);
    setUser(safeUser);
    return safeUser;
  };
  const register = async (email, password, name, botCheck = {}) => {
    const u = await call("/auth/register", { email, password, name, ...botCheck });
    const safeUser = saveSession(u);
    setUser(safeUser);
    return safeUser;
  };
  const logout = async () => {
    try { await call("/auth/logout"); } catch { /* ignore */ }
    window.localStorage.removeItem(SESSION_KEY);
    setUser(false);
  };
  const startGoogle = (returnPath = "/chat") => {
    const safePath = typeof returnPath === "string" && returnPath.startsWith("/") && !returnPath.startsWith("//")
      ? returnPath
      : "/chat";
    const attribution = captureAttribution();
    const query = new URLSearchParams({ return_path: safePath });
    if (Object.keys(attribution).length) query.set("campaign_data", JSON.stringify(attribution));
    window.location.href = `${API}/auth/google/start?${query}`;
  };
  const refreshUser = checkAuth;

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, startGoogle, refreshUser, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
